import {
  DocumentChartBarIcon,
  UserGroupIcon,
  BanknotesIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  PresentationChartLineIcon,
  CalendarDaysIcon,
  UserIcon,
  ChartPieIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../Auth/Auth';
import BreadCrumb from '../BreadCrumb';
import MoonLoader from 'react-spinners/MoonLoader';

const pages = [
  { name: 'Analytics & Reports', to: '/analytics', component: Link, current: true },
];

const actions = [
  {
    title: 'Job Card Status',
    to: '/analytics/jobs',
    desc: "Open and Closed Job Cards",
    icon: DocumentChartBarIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-blue-500 to-cyan-600',
  },
  {
    title: 'Customer Job History',
    to: '/analytics/customers',
    desc: "Details of Customers and their Jobs",
    icon: UserGroupIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-purple-500 to-indigo-600',
  },
  {
    title: 'Revenue By Service Type',
    to: '/analytics/revenue',
    desc: "Total, Commission and Product Sales",
    icon: CurrencyDollarIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-emerald-500 to-teal-600',
  },
  {
    title: 'Customer Request Trends',
    to: '/analytics/servicedemand',
    desc: "Frequently Requested Services by Customers",
    icon: ChartBarIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-orange-500 to-amber-600',
  },
  {
    title: 'Financial Analysis',
    to: '/analytics/financial',
    desc: "Outstanding Invoice and Payment Status",
    icon: BanknotesIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-rose-500 to-pink-600',
  },
  {
    title: 'Customer Insights',
    to: '/analytics/customers',
    desc: "Segmentation and Loyalty Analysis",
    icon: ChartPieIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-violet-500 to-purple-600',
  },
  {
    title: 'Operational Analytics',
    to: '/analytics/operations',
    desc: "Job Status and Performance",
    icon: PresentationChartLineIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-lime-500 to-green-600',
  },
  {
    title: 'Monthly Job Report',
    to: '/analytics/monthly-jobs',
    desc: 'Jobs opened/approved/closed by month',
    icon: CalendarDaysIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-sky-500 to-blue-600',
  },
  {
    title: 'Agent Performance',
    to: '/analytics/agents',
    desc: "View Agent Commission and Activity levels",
    icon: UserIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-fuchsia-500 to-pink-600',
  },
  {
    title: 'Weekly Management Report',
    to: '/analytics/weekly-report',
    desc: 'Weekly financial overview with Excel export',
    icon: ClipboardDocumentListIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-teal-500 to-cyan-600',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Analytics = () => {
  const { role, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin' && role !== 'Finance') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <ChartBarIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
                <p className="text-sm text-gray-600 mt-1">Comprehensive insights into business performance and operations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {actions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              {/* Gradient Background Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              {/* Card Content */}
              <div className="relative p-8">
                {/* Icon Container */}
                <div className="flex items-center justify-center mb-6">
                  <div
                    className={classNames(
                      action.iconBackground,
                      'rounded-2xl p-4 shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3'
                    )}
                  >
                    <action.icon className={classNames(action.iconForeground, "h-8 w-8")} aria-hidden="true" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-center text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors duration-200">
                  {action.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-center text-gray-600 leading-relaxed">
                  {action.desc}
                </p>

                {/* Arrow Icon */}
                <div className="absolute bottom-6 right-6 text-gray-400 group-hover:text-indigo-600 transition-colors duration-200">
                  <svg className="h-6 w-6 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>

              {/* Bottom Accent Bar */}
              <div className={classNames(
                action.iconBackground,
                "absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              )}></div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
